<script setup lang="ts">
import {
  tpgIconContentCopy,
  tpgIconDelete,
  tpgIconEdit,
  tpgIconIosShare,
  tpgIconLayers,
  tpgIconLayersFull,
  tpgIconLockOpen,
  tpgIconMap,
  tpgIconPadlock,
  tpgIconStar,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import type { GrammarMenuItemProps } from '@nora-monorepo/grammar-ui';
import { GrammarModal } from '@nora-monorepo/grammar-ui';
import type { DrawLayerResponseDTO } from '@nora-monorepo/nora-api-client';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';
import { BBox } from '@nora-monorepo/shared';
import { useConfirmModalStore, useTextPromptModalStore, useToastStore } from '@nora-monorepo/ui-shared';

import OperationPicker from '#nora-ui/components/operation-picker/OperationPicker.vue';
import MenuItemRow from '#nora-ui/map/components/overlay-menu/primitives/MenuItemRow.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useDrawLayerApiStore } from '#nora-ui/stores/api/draw-layer-api-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';
import { operationCan } from '#nora-ui/utils/permission';

interface DrawLayerMenuItemProps {
  drawLayer: DrawLayerResponseDTO;
  operationId: string;
  mapId: string;
}

interface DrawLayerMenuItemEmits {
  dragStart: [event: DragEvent];
  dragEnd: [];
}

const props = defineProps<DrawLayerMenuItemProps>();
const emits = defineEmits<DrawLayerMenuItemEmits>();

const { t } = useI18n();
const mapStore = useMapStore();
const confirmModalStore = useConfirmModalStore();
const textPromptModalStore = useTextPromptModalStore();
const toastStore = useToastStore();
const drawLayerApiStore = useDrawLayerApiStore();
const operationStore = useOperationStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const { drawLayer } = toRefs(props);
const showOperationsPicker = ref(false);
const operationsPickerAction = ref<'copy' | 'move'>();
const { operations } = storeToRefs(operationStore);

function toggleSelected(selected: boolean) {
  if (drawLayer.value.type === DrawLayerTypeEnum.GEOFENCE) {
    return;
  }

  if (selected) {
    // Selecting an operation's draw layer also makes that operation the draw target.
    map.setActiveOperation(props.operationId);
    map.state.draw.setActiveDrawLayer(drawLayer.value.id);
  }
  else if (map.state.draw.activeDrawLayerId.value === drawLayer.value.id) {
    map.state.draw.clearActiveDrawLayer();
  }
}

function onDrawLayerVisibilityChanged(visible: boolean) {
  map.state.draw.setDrawLayerVisibility(drawLayer.value.id, visible);
}

function onDragStart(event: DragEvent): void {
  emits('dragStart', event);
}

function onDragEnd(): void {
  emits('dragEnd');
}

function onRenameClick() {
  textPromptModalStore.showTextPromptModal({
    title: 'Draw layer',
    textFieldLabel: 'Draw layer name',
    placeholder: t('conversationDialog.titlePlaceholder'),
    initialValue: drawLayer.value.name,
    onConfirm: async (newName: string) => {
      try {
        await drawLayerApiStore.updateDrawLayerMetadata(
          drawLayer.value.id,
          props.operationId,
          { name: newName.trim() },
        );
      }
      catch {
        toastStore.addError({
          message: t('drawLayer.updateDrawLayerFailedTitle'),
          detail: t('drawLayer.updateDrawLayerFailedMessage'),
        });
      }
    },
  });
}

async function onDrawLayerLockClick() {
  await map.state.draw.toggleDrawLayerLock(drawLayer.value.id);
}

async function onDrawLayerGotoBBoxClick() {
  const response = await drawLayerApiStore.getOperationDrawLayerBBox(drawLayer.value.id, props.operationId);

  if (!response) {
    toastStore.addError({
      message: 'Unable to get draw layer bounding box',
      detail: 'The draw layer may be empty.',
    });

    return;
  }

  const bbox = BBox.fromBboxArray(response.bbox);

  map.state.draw.setDrawLayerMenuOpen(false);

  map.fitToBBox(
    bbox,
  );
}

function onCopyToOperationClick() {
  showOperationsPicker.value = true;
  operationsPickerAction.value = 'copy';
}

function onMoveToOperationClick() {
  showOperationsPicker.value = true;
  operationsPickerAction.value = 'move';
}

const onExportClick = () => map.state.draw.exportDrawLayerById(drawLayer.value.id);

function onDeleteClick() {
  confirmModalStore.showConfirmModal({
    headerText: 'Delete Draw Layer',
    bodyText: `Are you sure you want to delete the draw layer "${drawLayer.value.name}"?`,
    isDelete: true,
    onConfirm: async () => {
      await map.state.draw.deleteDrawLayerById(drawLayer.value.id);
    },
  });
}

async function onSetDefaultClick() {
  const success = await map.state.draw.setDefaultDrawLayer(drawLayer.value.id);

  if (!success) {
    toastStore.addError({
      message: 'Unable to set default draw layer',
      detail: 'Something went wrong while setting the default draw layer.',
    });
  }
}

function onPublishClick() {
  confirmModalStore.showConfirmModal({
    headerText: 'Publish Draw Layer',
    bodyText: `Are you sure you want to publish the draw layer "${drawLayer.value.name}"? It will become visible to all operation members.`,
    onConfirm: async () => {
      await map.state.draw.publishDrawLayer(drawLayer.value.id);
    },
  });
}

const menuItems = computed(() => {
  const items: GrammarMenuItemProps[] = [
    {
      label: 'Rename',
      onSelect: onRenameClick,
      leadingIconTemplate: tpgIconEdit,
    },
  ];

  if (drawLayer.value.draft) {
    items.push({
      label: 'Publish',
      onSelect: onPublishClick,
      leadingIconTemplate: tpgIconIosShare,
    });
  }

  if (props.drawLayer.type !== DrawLayerTypeEnum.GEOFENCE) {
    items.push({
      label: drawLayer.value.locked ? 'Unlock' : 'Lock',
      onSelect: onDrawLayerLockClick,
      leadingIconTemplate: drawLayer.value.locked
        ? tpgIconLockOpen
        : tpgIconPadlock,
    });

    if (
      !drawLayer.value.isDefault
      && !drawLayer.value.draft
      && operationCan('update:nexus.operation', props.operationId)
    ) {
      items.push({
        label: 'Set as default',
        onSelect: onSetDefaultClick,
        leadingIconTemplate: tpgIconStar,
      });
    }
  }

  items.push({
    label: 'Go to draw layer',
    onSelect: onDrawLayerGotoBBoxClick,
    leadingIconTemplate: tpgIconMap,
  });

  if (props.drawLayer.type !== DrawLayerTypeEnum.GEOFENCE) {
    items.push({
      label: 'Copy to operation',
      onSelect: onCopyToOperationClick,
      leadingIconTemplate: tpgIconContentCopy,
    });

    // The default draw layer cannot be moved out of its operation (backend rejects it).
    if (!drawLayer.value.isDefault) {
      items.push({
        label: 'Move to operation',
        onSelect: onMoveToOperationClick,
        leadingIconTemplate: tpgIconLayers,
      });
    }
  }

  items.push(
    {
      label: 'Export',
      onSelect: onExportClick,
      leadingIconTemplate: tpgIconIosShare,
    },
  );

  // The default draw layer cannot be deleted (backend rejects it).
  if (props.drawLayer.type !== DrawLayerTypeEnum.GEOFENCE && !drawLayer.value.isDefault) {
    items.push({
      label: 'Delete',
      onSelect: onDeleteClick,
      leadingIconTemplate: tpgIconDelete,
    });
  }

  return items;
});

async function onOperationSelected(operationId: string) {
  if (!operationsPickerAction.value) {
    return;
  }
  if (operationsPickerAction.value === 'copy') {
    await drawLayerApiStore.copyOperationDrawLayerToOperation(
      drawLayer.value.id,
      props.operationId,
      operationId,
    );
  }
  else {
    await drawLayerApiStore.moveOperationDrawLayerToOperation(
      drawLayer.value.id,
      props.operationId,
      operationId,
    );
  }

  showOperationsPicker.value = false;
}

const isSelected = computed(() => {
  return map.state.draw.activeDrawLayerId.value === drawLayer.value.id;
});
</script>

<template>
  <MenuItemRow
    :title="drawLayer.name"
    :selected="isSelected"
    :menu-items="menuItems"
    :visible="map.state.draw.isDrawLayerVisible(drawLayer.id)"
    :locked="drawLayer.locked"
    selectable
    draggable
    :icon="tpgIconLayersFull"
    :draw-layer="drawLayer"
    @tpg-select="toggleSelected"
    @visibility-changed="onDrawLayerVisibilityChanged"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
  />

  <GrammarModal
    :is-open="showOperationsPicker"
    :label="
      operationsPickerAction === 'copy'
        ? 'Copy to operation'
        : 'Move to operation'
    "
    show-close-button
    @close="showOperationsPicker = false"
  >
    <template #content>
      <div class="pb-6">
        <OperationPicker
          :operations="operations"
          @select-operation="onOperationSelected"
        />
      </div>
    </template>
  </GrammarModal>
</template>
